import React, { useEffect, useState } from 'react';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { eventNames, reportToSegment, types } from '@smartcar/morse';
import { arrayOf, func, object, shape, string } from 'prop-types';
import { Link } from 'react-router-dom';

import { ApplicationLabel, Modal, PermissionGate, RegisterApplication, UpsellModal } from '../../../../components';
import application from '../../../../localization/Application/application';
import formatDate from '../../../../services/applications/utils/dateFormatter';
import { gatedFeatureData } from '../../../../services/featureGate';
import FEATURES from '../../../../services/featureGate/features';
import ApplicationHeader from '../ApplicationHeader';

import staticText from '../../../../localization/Application/applications';

const Applications = ({
  applications,
  lockedApplications,
  actions: {
    selectApplication,
  },
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [lockedAppClicked, setLockedAppClicked] = useState(false);
  const { featureSetId } = applications[Object.keys(applications)[0]];
  const maxAppCountReached = Object.keys(applications).length >=
    gatedFeatureData(FEATURES.APPLICATIONS, featureSetId);

  /* istanbul ignore next */
  const toggleRegisterAppModal = () => {
    const segmentReport = maxAppCountReached ? application.upgradeModal : application.modal;
    reportToSegment(types.TRACK, eventNames[`modal${modalOpen ? 'Closed' : 'Opened'}`], segmentReport);
    setModalOpen(!modalOpen);
  };

  const modalConfig = {
    title: application.modal.title,
    content: (
      <RegisterApplication
        toggleModal={toggleRegisterAppModal}
        applicationNames={Object.values(applications).map(app => app.name)}
      />
    ),
  };

  const handleAppLinkClick = (appId, appName, appIsLocked) => {
    if (appIsLocked) {
      setModalOpen(true);
      setLockedAppClicked(true);
      const segmentReport = application.upgradeModal;
      reportToSegment(types.TRACK, eventNames[`modal${lockedAppClicked ? 'Closed' : 'Opened'}`],
        segmentReport);
    } else {
      selectApplication(appId);
      reportToSegment(types.TRACK, eventNames.linkClicked, {
        section: 'article',
        style: 'inline',
        path: `/apps/${appId}/`,
        text: appName,
      });
    }
  };

  const getAppNameCell = (app, locked) => {
    return locked ? (
      <Button variant="unstyled" onClick={() => handleAppLinkClick(app.id, app.name, locked)}>
        <ApplicationLabel
          application={app}
          locked
        />
      </Button>
    ) : (
      <Link to={`/apps/${app.id}`} onClick={() => handleAppLinkClick(app.id, app.name, locked)}>
        <ApplicationLabel application={app} />
      </Link>
    );
  };

  useEffect(() => {
    reportToSegment(types.PAGE, 'Applications');
  }, []);

  return (
    <div>
      <ApplicationHeader
        heading={staticText.heading}
        rightSideContent={(
          <PermissionGate
            dashboardPermission="write_team" // temporary solution until we add a specific permission like create_applications
          >
            <Button
              variant="outlined"
              onClick={toggleRegisterAppModal}
            >
              {staticText.add}
            </Button>
          </PermissionGate>
        )}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                {staticText.table.headers.name}
              </TableCell>
              <TableCell>
                {staticText.table.headers.id}
              </TableCell>
              <TableCell>
                {staticText.table.headers.createdAt}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(applications).map((app) => {
              const locked = lockedApplications.includes(app.id);
              return (
                <TableRow key={app.id} className={locked ? 'disabled' : ''}>
                  <TableCell>
                    {getAppNameCell(app, locked)}
                  </TableCell>
                  <TableCell>{app.id}</TableCell>
                  <TableCell>{formatDate(app.createdAt)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {modalOpen && !maxAppCountReached &&
        <Modal
          {...modalConfig}
          toggleModal={toggleRegisterAppModal}
        />
      }
      {modalOpen && (maxAppCountReached || lockedAppClicked) &&
        <UpsellModal
          toggleModal={toggleRegisterAppModal}
          feature={FEATURES.APPLICATIONS}
        />
      }
    </div>
  );
};

export default Applications;

Applications.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  applications: object.isRequired,
  lockedApplications: arrayOf(string),
  actions: shape({
    selectApplication: func.isRequired,
  }).isRequired,
};

Applications.defaultProps = {
  lockedApplications: [],
};
